/**
 * Medal ribbons as CSS stripes (05 §10). Each medal is a list of `[token, units]` stripes;
 * `ribbonGradient` turns it into a hard-edged linear-gradient. The medal's name and citation
 * come from `GameApi.describe` and ride on the ribbon as its label and tooltip.
 */

import './insignia.css';
import type { MedalId } from '../../../meta/types';
import { el } from '../dom';
import type { Describe } from '../text';
import { safeDescribe } from '../text';

type Token = 'amber' | 'green' | 'red' | 'red-hi' | 'blue' | 'ice' | 'ink' | 'ink-2' | 'paper' | 'olive' | 'warn';
type Stripe = readonly [token: Token, units: number];

export const RIBBONS: Readonly<Record<MedalId, readonly Stripe[]>> = {
  'commendation-1': [['olive', 3], ['paper', 1], ['olive', 4], ['paper', 1], ['olive', 3]],
  'commendation-2': [['olive', 3], ['paper', 1], ['olive', 1], ['amber', 2], ['olive', 1], ['paper', 1], ['olive', 3]],
  'bronze-star': [['red', 4], ['paper', 1], ['blue', 2], ['paper', 1], ['red', 4]],
  'silver-star': [['blue', 2], ['paper', 2], ['red', 4], ['paper', 2], ['blue', 2]],
  dsc: [['red', 1], ['paper', 1], ['blue', 8], ['paper', 1], ['red', 1]],
  'medal-of-honor': [['ice', 4], ['paper', 1], ['ice', 2], ['paper', 1], ['ice', 4]],
  'purple-heart': [['paper', 1], ['ink-2', 10], ['paper', 1]],
  'pow-medal': [['red', 1], ['paper', 1], ['blue', 1], ['ink', 6], ['blue', 1], ['paper', 1], ['red', 1]],
  'evaders-ribbon': [['olive', 4], ['amber', 1], ['ink', 2], ['amber', 1], ['olive', 4]],
  'pathfinder-badge': [['amber', 3], ['ink', 6], ['amber', 3]],
};

/** `linear-gradient(90deg, ...)` with hard stops, colours as `var(--menu-*)` tokens. Pure. */
export function ribbonGradient(medal: MedalId): string {
  const stripes = RIBBONS[medal];
  const total = stripes.reduce((sum, [, units]) => sum + units, 0);
  let at = 0;
  const stops = stripes.map(([token, units]) => {
    const from = (at / total) * 100;
    at += units;
    const to = (at / total) * 100;
    return `var(--menu-${token}) ${from.toFixed(1)}% ${to.toFixed(1)}%`;
  });
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

/** One ribbon; `large` is the size pinned at the debrief. */
export function ribbon(medal: MedalId, describe: Describe, large = false): HTMLElement {
  const d = safeDescribe(describe, medal);
  const node = el('span', large ? 'menu-ribbon menu-ribbon-large' : 'menu-ribbon');
  node.style.background = ribbonGradient(medal);
  node.title = d.text === '' ? d.name : `${d.name} - ${d.text}`;
  node.setAttribute('role', 'img');
  node.setAttribute('aria-label', d.name);
  return node;
}

/** The rack on a dossier, senior medals first as awarded order is not seniority. */
export function ribbonRack(medals: readonly MedalId[], describe: Describe): HTMLElement {
  const rack = el('div', 'menu-ribbons');
  if (medals.length === 0) {
    rack.append(el('span', 'menu-dim menu-small', 'NO DECORATIONS'));
    return rack;
  }
  const order = Object.keys(RIBBONS);
  const sorted = [...medals].sort((a, b) => seniority(order, a) - seniority(order, b));
  for (const medal of sorted) rack.append(ribbon(medal, describe));
  return rack;
}

/** Medal of Honor first, then DSC, Silver Star, Bronze Star; the rest in table order. */
function seniority(order: readonly string[], medal: MedalId): number {
  const top: readonly MedalId[] = ['medal-of-honor', 'dsc', 'silver-star', 'bronze-star'];
  const i = top.indexOf(medal);
  return i >= 0 ? i : top.length + order.indexOf(medal);
}
